// src/components/assets/CSVImport.tsx
import { useState, useRef } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { assetActions } from '../../stores/assetStore';
import type { Asset } from '../../types/asset';

interface CSVImportProps {
  isOpen: boolean;
  onClose: () => void;
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
  existingItems: Asset[];
}

export function CSVImport({ isOpen, onClose, showToast, existingItems }: CSVImportProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const rawCsv = event.target?.result as string;
        // Strip BOM character that Excel/Windows CSV files often include
        const csv = rawCsv.replace(/^\uFEFF/, '');
        const lines = csv.split(/\r?\n/);

        // Parse headers using same quote-aware logic as data rows
        const parseCSVRow = (line: string): string[] => {
          const result: string[] = [];
          let current = '';
          let inQuotes = false;

          for (const char of line) {
            if (char === '"') {
              inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
              result.push(current.trim().replace(/"/g, ''));
              current = '';
            } else {
              current += char;
            }
          }
          result.push(current.trim().replace(/"/g, ''));
          return result;
        };

        const headers = parseCSVRow(lines[0]);

        console.log('CSV Headers:', headers);

        const imported: Asset[] = [];
        let skipped = 0;

        // Reference numbers are now generated server-side for concurrency safety
        // Each imported asset gets a placeholder that the server replaces

        for (let i = 1; i < lines.length; i++) {
          if (!lines[i].trim()) continue;

          const values = parseCSVRow(lines[i]);

          const getValue = (index: number) => values[index] || '';

          const make = getValue(headers.indexOf('Make'));
          const model = getValue(headers.indexOf('Model'));
          const assetGroup = getValue(headers.indexOf('Asset Group'));
          const sku = getValue(headers.indexOf('SKU'));

          if (!sku && !make && !model) {
            skipped++;
            continue;
          }

          // Duplicate detection only when SKU is provided
          if (sku) {
            const isDuplicate = existingItems.some(
              item => item.sku && item.sku.toLowerCase() === sku.toLowerCase()
            ) || imported.some(
              item => item.sku && item.sku.toLowerCase() === sku.toLowerCase()
            );

            if (isDuplicate) {
              skipped++;
              continue;
            }
          }

          const assetOwner = getValue(headers.indexOf('Asset Owner')) || getValue(headers.indexOf('Owner'));
          const now = new Date().toISOString();
          const displayName = [make, model].filter(Boolean).join(' ') || 'Imported asset';

          const asset: Asset = {
            id: `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            referenceNumber: '',
            make,
            model,
            assetGroup,
            sku: sku || '',
            description: getValue(headers.indexOf('Description')) || `${displayName} - Imported asset`,
            owner: assetOwner,
            assetOwner: assetOwner,
            location: getValue(headers.indexOf('Location')),
            status: getValue(headers.indexOf('Status')) || 'Active',
            quantity: parseInt(getValue(headers.indexOf('Quantity'))) || 1,
            acquisitionDate: getValue(headers.indexOf('Acquisition Date')) || now.split('T')[0],
            cost: getValue(headers.indexOf('Cost')) || '0',
            maintenanceSchedule: getValue(headers.indexOf('Maintenance Schedule')) || 'Annual',
            lastInspectionDate: getValue(headers.indexOf('Last Inspection Date')) || '',
            nextInspectionDate: getValue(headers.indexOf('Next Inspection Date')) || '',
            isISO27001: getValue(headers.indexOf('ISO27001')).toLowerCase() === 'yes' ||
                        getValue(headers.indexOf('ISO 27001')).toLowerCase() === 'yes',
            iso27001Category: getValue(headers.indexOf('ISO27001 Category')) ||
                              getValue(headers.indexOf('ISO 27001 Category')),
            auditDate: getValue(headers.indexOf('Last Audit Date')) ||
                       getValue(headers.indexOf('Audit Date')),
            auditPeriodicity: getValue(headers.indexOf('Audit Periodicity (months)')) ||
                              getValue(headers.indexOf('Audit Periodicity')),
            calibrationDate: getValue(headers.indexOf('Calibration Date')),
            calibrationPeriodicity: getValue(headers.indexOf('Calibration Periodicity (months)')) ||
                                    getValue(headers.indexOf('Calibration Periodicity')),
            certificationDate: getValue(headers.indexOf('Certification Date')),
            certificationPeriodicity: getValue(headers.indexOf('Certification Periodicity (months)')) ||
                                      getValue(headers.indexOf('Certification Periodicity')),
            testDate: getValue(headers.indexOf('Test Date')),
            testPeriodicity: getValue(headers.indexOf('Test Periodicity (months)')) ||
                             getValue(headers.indexOf('Test Periodicity')),
            notes: getValue(headers.indexOf('Notes')),
            datesApplicable: true,
            createdAt: now,
            updatedAt: now,
          };

          imported.push(asset);
        }

        // Import assets sequentially to avoid overwhelming the server
        for (const asset of imported) {
          await assetActions.addAsset(asset);
        }

        showToast(
          `Imported ${imported.length} assets${skipped > 0 ? ` (${skipped} skipped)` : ''}`,
          'success'
        );
        onClose();

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        console.error('Import error:', error);
        showToast('Error parsing CSV file', 'error');
      } finally {
        setImporting(false);
      }
    };

    reader.onerror = () => {
      showToast('Error reading file', 'error');
      setImporting(false);
    };

    reader.readAsText(file);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Import Assets"
      size="md"
      gradient="from-green-600 to-teal-600"
    >
      <div className="space-y-4">
        <p className="text-gray-700">
          Upload a CSV file. At least one of <strong>Make</strong>, <strong>Model</strong>, or <strong>SKU</strong> is required — all other columns are optional.
        </p>

        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 max-h-40 overflow-y-auto">
          <code className="text-sm text-gray-800">
            Make, Model, Asset Group, SKU, Quantity, Location, Asset Owner, ISO27001, ISO27001 Category,
            Last Audit Date, Audit Periodicity (months), Calibration Date, Calibration Periodicity (months),
            Certification Date, Certification Periodicity (months), Test Date, Test Periodicity (months), Notes
          </code>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Notes:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>At least one of <strong>Make</strong>, <strong>Model</strong>, or <strong>SKU</strong> is required — other columns are beneficial but optional</li>
                <li>If SKU is empty, the reference number will be used</li>
                <li>Duplicate SKUs will be skipped</li>
                <li>ISO27001 should be "Yes" or "No"</li>
                <li>Reference numbers are auto-generated</li>
                <li>Export existing data to see exact format</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-500 transition">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleImport}
            className="hidden"
            id="csv-upload"
            disabled={importing}
          />
          <label
            htmlFor="csv-upload"
            className="cursor-pointer flex flex-col items-center gap-2"
          >
            <Icon name="Upload" size={32} className="text-gray-400" />
            <span className="text-gray-600">
              {importing ? 'Importing...' : 'Click to select CSV file'}
            </span>
          </label>
        </div>

        <Button variant="secondary" onClick={onClose} className="w-full">
          Cancel
        </Button>
      </div>
    </Modal>
  );
}
